import { z } from 'zod';

const API_URL = __API_URL__;

/**
 * Eroare de la server, cu codul păstrat.
 *
 * Serverul trimite un `code` stabil (`invalid_credentials`, `account_locked`,
 * `missing_permission`) tocmai ca frontend-ul să nu compare texte. Un `Error` simplu ar
 * fi pierdut codul și ar fi obligat formularul de login să ghicească din mesaj.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /**
     * Greșelile pe câmpuri, când serverul le-a trimis (`code: 'validation_error'`).
     *
     * Se păstrează, nu se topesc în `message`: „Datele trimise nu sunt valide" într-un
     * toast nu spune operatorului *ce* să corecteze, iar el se uită la un formular cu
     * șapte câmpuri. Vezi `ZodValidationPipe` din `apps/api`.
     */
    readonly issues: readonly FieldIssue[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Mesajul pentru un câmp anume, sau `undefined` dacă eroarea nu-l privește. */
  issueFor(field: string): string | undefined {
    return this.issues.find((issue) => issue.path === field)?.message;
  }
}

export interface FieldIssue {
  readonly path: string;
  readonly message: string;
}

const errorBodySchema = z.object({
  code: z.string().optional(),
  message: z.union([z.string(), z.array(z.string())]).optional(),
  issues: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
});

async function toApiError(response: Response, path: string): Promise<ApiError> {
  // Corpul de eroare e „ce a trimis serverul", deci se citește apărat: un 502 de la un
  // proxy întoarce HTML, iar un `json()` care aruncă ar ascunde statusul real.
  const parsed = await response
    .json()
    .then((body: unknown) => errorBodySchema.safeParse(body))
    .catch(() => null);

  const body = parsed?.success ? parsed.data : undefined;
  const message = Array.isArray(body?.message) ? body.message.join(' ') : body?.message;

  return new ApiError(
    response.status,
    body?.code ?? 'unknown_error',
    message ?? `${path} a răspuns ${response.status}`,
    body?.issues ?? [],
  );
}

/**
 * Zod pe graniță, în ambele sensuri.
 *
 * Nu se face `as SomeType` peste `response.json()`: un câmp redenumit pe server ar
 * produce `undefined` în UI, tăcut, până când cineva se uită la un contract tipărit.
 *
 * `credentials: 'include'` peste tot: sesiunea e un cookie `HttpOnly`, deci pagina nu-l
 * poate atașa singură — trebuie să-l trimită browserul.
 */
export async function apiGet<TSchema extends z.ZodType>(
  path: string,
  schema: TSchema,
): Promise<z.infer<TSchema>> {
  const response = await fetch(`${API_URL}/api${path}`, {
    headers: { Accept: 'application/json' },
    credentials: 'include',
  });

  if (!response.ok) {
    throw await toApiError(response, `GET ${path}`);
  }

  return schema.parse(await response.json());
}

async function apiSend<TSchema extends z.ZodType>(
  method: 'POST' | 'PATCH' | 'PUT',
  path: string,
  body: unknown,
  schema: TSchema,
): Promise<z.infer<TSchema>> {
  const response = await fetch(`${API_URL}/api${path}`, {
    method,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw await toApiError(response, `${method} ${path}`);
  }

  return schema.parse(await response.json());
}

export function apiPost<TSchema extends z.ZodType>(
  path: string,
  body: unknown,
  schema: TSchema,
): Promise<z.infer<TSchema>> {
  return apiSend('POST', path, body, schema);
}

/** Modificare parțială. Serverul expune `PATCH` acolo unde se trimite doar ce s-a schimbat. */
export function apiPatch<TSchema extends z.ZodType>(
  path: string,
  body: unknown,
  schema: TSchema,
): Promise<z.infer<TSchema>> {
  return apiSend('PATCH', path, body, schema);
}

/**
 * Înlocuire. Serverul expune `PUT` acolo unde valoarea trimisă e starea completă a
 * resursei, iar a doua trimitere identică trebuie să aibă același efect ca prima —
 * alegerea firmei active, de exemplu.
 */
export function apiPut<TSchema extends z.ZodType>(
  path: string,
  body: unknown,
  schema: TSchema,
): Promise<z.infer<TSchema>> {
  return apiSend('PUT', path, body, schema);
}

/**
 * Ștergere. Serverul răspunde `204`, fără corp — deci nu există schemă de validat, iar
 * `response.json()` ar arunca pe corp gol.
 */
export async function apiDelete(path: string): Promise<void> {
  const response = await fetch(`${API_URL}/api${path}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json' },
    credentials: 'include',
  });

  if (!response.ok) {
    throw await toApiError(response, `DELETE ${path}`);
  }
}

/**
 * Descarcă un fișier binar.
 *
 * Nu trece prin `apiGet`: acela cere `schema.parse(response.json())`, iar un PDF nu e
 * JSON. Corpul de eroare **rămâne** JSON, deci `toApiError` se refolosește — un 503 de la
 * serviciul de randare ajunge cu mesajul lui, nu ca „a eșuat".
 */
export async function apiGetBlob(path: string): Promise<Blob> {
  const response = await fetch(`${API_URL}/api${path}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw await toApiError(response, `GET ${path}`);
  }

  return response.blob();
}

/** Ca `apiGetBlob`, dar cu un corp de trimis: previzualizarea cere HTML-ul documentului. */
export async function apiPostBlob(path: string, body: unknown): Promise<Blob> {
  const response = await fetch(`${API_URL}/api${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw await toApiError(response, `POST ${path}`);
  }

  return response.blob();
}

/**
 * Adresa absolută a unui fișier servit de API, pentru `<img src>` sau un link.
 *
 * `API_URL` rămâne neexportat — un `${API_URL}` scris prin ecrane ar ocoli tăcut `/api`.
 *
 * ⚠️ **Autentificarea merge pe cookie, nu pe `credentials: 'include'`**: un `<img>` nu
 * trece prin `fetch`. Funcționează pentru că apiul și interfața sunt pe același site
 * (`api.pcrm.local` și `crm.pcrm.local`, sau două porturi de `localhost`), iar cookie-ul e
 * `SameSite=Lax`. Mutat pe un domeniu diferit, imaginea ar veni 401 — atunci se citește cu
 * `apiGetBlob` și se randează dintr-un object URL.
 */
export function apiFileUrl(path: string): string {
  return `${API_URL}/api${path}`;
}

/**
 * Trimite un fișier, `multipart/form-data`.
 *
 * ⚠️ **Fără `Content-Type` scris de mână.** Browserul îl pune singur, cu `boundary`-ul cu
 * tot; scris de noi, ar lipsi boundary-ul și serverul ar refuza corpul fără să spună de ce.
 *
 * `fields` merge alături de fișier, în același corp: la încărcarea unui document, clientul
 * și tipul actului sunt parte din aceeași cerere ca fișierul — două cereri ar fi însemnat
 * un fișier scris pe disc pentru o a doua care nu mai vine. ⚠️ **Toate ajung ca text**,
 * oricum ar fi fost scrise aici; schemele de pe server le convertesc înapoi.
 */
export async function apiUpload<TSchema extends z.ZodType>(
  path: string,
  file: File,
  schema: TSchema,
  fields: Readonly<Record<string, string>> = {},
): Promise<z.infer<TSchema>> {
  const form = new FormData();

  form.append('file', file);

  for (const [name, value] of Object.entries(fields)) {
    form.append(name, value);
  }

  const response = await fetch(`${API_URL}/api${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
    body: form,
  });

  if (!response.ok) {
    throw await toApiError(response, `POST ${path}`);
  }

  return schema.parse(await response.json());
}

const componentStateSchema = z.enum(['up', 'down']);

export const healthSchema = z.object({
  status: z.enum(['ok', 'degraded']),
  database: componentStateSchema,
  pdf: componentStateSchema,
  /** Workerul de joburi programate și joburile lui, doar „merge / nu merge" (#215). */
  worker: componentStateSchema,
  jobs: componentStateSchema,
});

export type Health = z.infer<typeof healthSchema>;

export function fetchHealth(): Promise<Health> {
  return apiGet('/health', healthSchema);
}

export const jobsStatusSchema = z.object({
  worker: z.object({ state: componentStateSchema, lastBeatAt: z.string().nullable() }),
  jobs: z.array(
    z.object({
      job: z.string(),
      state: componentStateSchema,
      lastSucceededAt: z.string().nullable(),
      lastSummary: z.string().nullable(),
      lastFailure: z.object({ at: z.string(), error: z.string().nullable() }).nullable(),
    }),
  ),
});

export type JobsStatus = z.infer<typeof jobsStatusSchema>;

/**
 * Detaliile joburilor programate, pentru popup-ul stării sistemului (#215). Cu sesiune, nu ca
 * `/health`: mesajul de eroare al unui job poate conține detalii interne.
 */
export function fetchJobsStatus(): Promise<JobsStatus> {
  return apiGet('/health/jobs', jobsStatusSchema);
}

/**
 * Proba de randare: PDF generat de serviciul containerizat. Vezi tichetul #41.
 *
 * A fost butonul principal al paginii „Stare sistem"; de la #48 e acțiunea din spatele
 * indicatorului „Randare PDF" din sidebar; de la #215, al rândului lui din popup-ul stării
 * sistemului.
 */
export function fetchPdfProba(): Promise<Blob> {
  return apiGetBlob('/pdf/proba');
}
